import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  CreateGeoItemDocument,
  type CreateGeoItemMutation,
  type CreateGeoItemMutationVariables,
  GetAreaRecordGeoItemsDocument,
} from "~/features/recordGeoItems/gqls";
import type { RecordGeoItemEvidence, RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseRecordGeoItemCreateProps {
  recordId: string;
  onCreated: (geoItemId: string) => void;
}

type EvidenceWithReference = RecordGeoItemEvidence & { reference: { id: string; title: string } };

/** Create a new geo-item area for a record. Returns a submitter + `processing` flag. */
export function useRecordGeoItemCreate({ recordId, onCreated }: UseRecordGeoItemCreateProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [createGeoItem] = useMutation<CreateGeoItemMutation, CreateGeoItemMutationVariables>(CreateGeoItemDocument);

  const submit = useCallback(
    async (formData: RecordGeoItemFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        const res = await createGeoItem({
          // Refresh the record's area list so the new area shows without a manual reload (V1 parity).
          refetchQueries: [{ query: GetAreaRecordGeoItemsDocument, variables: { tenantId, recordId } }],
          awaitRefetchQueries: true,
          variables: {
            input: {
              tenantId,
              h3Indexes: formData.h3Indexes,
              geometry: formData.geometry,
              recordGeoItems: [
                {
                  entityOrRelationId: recordId,
                  startDate: formData.date,
                  endDate: formData.endDate,
                  i18nLabel: { ja: formData.jaLabel ?? undefined, en: formData.enLabel ?? undefined },
                  metadata: { memo: formData.memo ?? undefined },
                  evidences: formData.evidences
                    .filter((e): e is EvidenceWithReference => Boolean(e.reference?.id))
                    .map((e) => ({ id: e.id, referenceId: e.reference.id, citation: e.citation })),
                },
              ],
            },
          },
        });
        toast.success(t("feature.common.createdSuccessfully", { model: "geoItem" }));
        onCreated(res.data?.cnutCreateGeoItem?.id ?? "");
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [createGeoItem, onCreated, recordId, t, tenantId],
  );

  return { submit, processing };
}
